import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import { ContaServicoViewModel } from '../../../viewmodel/ContaServicoViewModel';
import { ProcessoViewModel } from '../../../viewmodel/ProcessoViewModel';
import useUnidadeStore from '../../../stores/unidadeStore';

interface AssociarContaServicoModalProps {
  show: boolean;
  processo: any;
  onClose: () => void;
  onSuccess: () => void;
}

const AssociarContaServicoModal: React.FC<AssociarContaServicoModalProps> = ({
  show,
  processo,
  onClose,
  onSuccess
}) => {
  const [contasServico, setContasServico] = useState<any[]>([]);
  const [contasAssociadas, setContasAssociadas] = useState<string[]>([]);
  const [selectedContaIds, setSelectedContaIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { unidades, fetchUnidades } = useUnidadeStore();

  useEffect(() => {
    if (show) {
      fetchUnidades();
      loadContasServico();
      loadContasAssociadas();
    }
  }, [show, fetchUnidades]);

  const loadContasAssociadas = async () => {
    try {
      const contas = await ProcessoViewModel.obterContasServicoProcesso(processo.id);
      const ids = contas.map((c: any) => c.clientId);
      setContasAssociadas(ids);
      setSelectedContaIds(ids);
    } catch (error) {
      console.error('Erro ao carregar contas associadas:', error);
    }
  };

  const loadContasServico = async () => {
    try {
      const response = await ContaServicoViewModel.getContasAtivasEAprovadas(0, 100);
      setContasServico(response.content || []);
    } catch (error) {
      toast.error(BackendErrorMessage(error));
    }
  };

  const toggleConta = (contaId: string) => {
    setSelectedContaIds(prev => 
      prev.includes(contaId) 
        ? prev.filter(id => id !== contaId)
        : [...prev, contaId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    try {
      const getPerfilNome = (conta: any) => conta.perfilNome || conta.perfil?.nome;
      
      const contasCandidatos = selectedContaIds.filter(id => {
        const perfil = getPerfilNome(contasServico.find(c => c.clientId === id));
        return perfil === 'INTEGRACAO_CANDIDATOS' || perfil === 'INTEGRACAO_CANDIDATOS_COMPLETO';
      });
      const contasFotos = selectedContaIds.filter(id => {
        const perfil = getPerfilNome(contasServico.find(c => c.clientId === id));
        return perfil === 'INTEGRACAO_CANDIDATOS_COMPLETO';
      });
      
      await ProcessoViewModel.atualizarContasServico(processo.id, contasCandidatos, contasFotos);
      toast.success('Contas de serviço atualizadas com sucesso!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(BackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Gerenciar Contas de Serviço</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="mb-3"><strong>Processo:</strong> {processo.nome}</p>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {contasServico.length === 0 ? (
              <p className="text-muted text-center my-3">Nenhuma conta de serviço disponível</p>
            ) : (
              <>
                <div className="mb-3">
                  <h6 className="mb-2">Integração Completa</h6>
                  <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px' }}>
                    {contasServico.filter(c => (c.perfilNome || c.perfil?.nome) === 'INTEGRACAO_CANDIDATOS_COMPLETO').length === 0 ? (
                      <small className="text-muted">Nenhuma conta disponível</small>
                    ) : (
                      contasServico
                        .filter(c => (c.perfilNome || c.perfil?.nome) === 'INTEGRACAO_CANDIDATOS_COMPLETO')
                        .map((conta) => {
                          const unidade = unidades.find(u => u.id === conta.unidadeId);
                          const isSelected = selectedContaIds.includes(conta.clientId);
                          return (
                            <Form.Check
                              key={conta.clientId}
                              type="checkbox"
                              id={`conta-${conta.clientId}`}
                              label={unidade?.nome || conta.descricao}
                              checked={isSelected}
                              onChange={() => toggleConta(conta.clientId)}
                              className="mb-2"
                            />
                          );
                        })
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <h6 className="mb-2">Integração de Candidatos</h6>
                  <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px' }}>
                    {contasServico.filter(c => (c.perfilNome || c.perfil?.nome) === 'INTEGRACAO_CANDIDATOS').length === 0 ? (
                      <small className="text-muted">Nenhuma conta disponível</small>
                    ) : (
                      contasServico
                        .filter(c => (c.perfilNome || c.perfil?.nome) === 'INTEGRACAO_CANDIDATOS')
                        .map((conta) => {
                          const unidade = unidades.find(u => u.id === conta.unidadeId);
                          const isSelected = selectedContaIds.includes(conta.clientId);
                          return (
                            <Form.Check
                              key={conta.clientId}
                              type="checkbox"
                              id={`conta-${conta.clientId}`}
                              label={unidade?.nome || conta.descricao}
                              checked={isSelected}
                              onChange={() => toggleConta(conta.clientId)}
                              className="mb-2"
                            />
                          );
                        })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          <small className="text-muted d-block mt-3">
            {selectedContaIds.length} conta(s) selecionada(s)
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AssociarContaServicoModal;
